/**
 * Rachawk Lookbook Engine
 * Lightweight page flip, lazy loading, editor overlay, user upload.
 * No dependencies. Pure vanilla JS.
 */

(function () {
    'use strict';

    /* =========================================
       UTILS
       ========================================= */

    function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
    function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
    function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

    /* =========================================
       PAGE FLIP ENGINE
       ========================================= */

    class PageFlipEngine {
        constructor(rootEl) {
            this.root = rootEl;
            this.book = qs('.lookbook__book', rootEl);
            if (!this.book) return;

            this.pages = qsa('.lookbook__page', this.book);
            this.totalSpreads = Math.ceil(this.pages.length / 2);
            this.currentSpread = 0;
            this.isFlipping = false;
            this.isMobile = window.innerWidth < 1024;

            // Mobile: linear page index (0, 1, 2, 3, ...)
            this.mobileCurrentPage = 0;

            // Read initial page from URL hash
            const hash = window.location.hash.replace('#page-', '');
            if (hash && !isNaN(parseInt(hash))) {
                const pageNum = parseInt(hash) - 1;
                this.currentSpread = clamp(Math.floor(pageNum / 2), 0, this.totalSpreads - 1);
                this.mobileCurrentPage = clamp(pageNum, 0, this.pages.length - 1);
            }

            this._bindEvents();
            this._render();
        }

        _bindEvents() {
            // Arrow buttons
            const prevBtn = qs('.lookbook__nav-arrow--prev', this.root);
            const nextBtn = qs('.lookbook__nav-arrow--next', this.root);
            if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
            if (nextBtn) nextBtn.addEventListener('click', () => this.next());

            // Click edges of book
            this.book.addEventListener('click', (e) => {
                const rect = this.book.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const threshold = rect.width * 0.15;
                if (x < threshold) this.prev();
                else if (x > rect.width - threshold) this.next();
            });

            // Touch swipe
            let startX = 0, startY = 0, tracking = false;
            this.book.addEventListener('touchstart', (e) => {
                startX = e.touches[0].clientX;
                startY = e.touches[0].clientY;
                tracking = true;
            }, { passive: true });

            this.book.addEventListener('touchend', (e) => {
                if (!tracking) return;
                tracking = false;
                const endX = e.changedTouches[0].clientX;
                const endY = e.changedTouches[0].clientY;
                const dx = endX - startX;
                const dy = endY - startY;
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
                    if (dx < 0) this.next(); else this.prev();
                }
            }, { passive: true });

            // Keyboard
            document.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowRight') this.next();
                if (e.key === 'ArrowLeft') this.prev();
            });

            // Page dots
            qsa('.lookbook__page-dot', this.root).forEach((dot, i) => {
                dot.addEventListener('click', () => this.goTo(i));
            });

            // Responsive
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    const wasMobile = this.isMobile;
                    this.isMobile = window.innerWidth < 1024;
                    if (wasMobile !== this.isMobile) {
                        // Sync state when switching modes
                        if (this.isMobile) {
                            this.mobileCurrentPage = this.currentSpread * 2;
                        } else {
                            this.currentSpread = Math.floor(this.mobileCurrentPage / 2);
                        }
                        this._render();
                    }
                }, 150);
            });
        }

        prev() {
            if (this.isFlipping) return;
            if (this.isMobile) {
                if (this.mobileCurrentPage <= 0) return;
                this._mobileFlip(this.mobileCurrentPage - 1);
            } else {
                if (this.currentSpread <= 0) return;
                this._flip(this.currentSpread - 1);
            }
        }

        next() {
            if (this.isFlipping) return;
            if (this.isMobile) {
                if (this.mobileCurrentPage >= this.pages.length - 1) return;
                this._mobileFlip(this.mobileCurrentPage + 1);
            } else {
                if (this.currentSpread >= this.totalSpreads - 1) return;
                this._flip(this.currentSpread + 1);
            }
        }

        goTo(spread) {
            if (this.isFlipping || spread === this.currentSpread) return;
            spread = clamp(spread, 0, this.totalSpreads - 1);
            if (this.isMobile) {
                // Jump to the first page of the target spread
                this._mobileFlip(spread * 2);
            } else {
                this._flip(spread);
            }
        }

        _flip(targetSpread) {
            this.isFlipping = true;
            this.book.classList.add('lookbook__book--is-flipping');
            const direction = targetSpread > this.currentSpread ? 'next' : 'prev';

            // Get current and target page pairs
            const currentLeft = this.pages[this.currentSpread * 2];
            const currentRight = this.pages[this.currentSpread * 2 + 1];
            const targetLeft = this.pages[targetSpread * 2];
            const targetRight = this.pages[targetSpread * 2 + 1];

            if (direction === 'next') {
                if (currentLeft) {
                    currentLeft.style.zIndex = '3';
                }
                if (currentRight) {
                    currentRight.style.zIndex = '5';
                    currentRight.classList.add('lookbook__page--flipping-next-out');
                }
                if (targetRight) {
                    targetRight.style.display = 'block';
                    targetRight.style.zIndex = '2';
                }
                if (targetLeft) {
                    targetLeft.style.display = 'block';
                    targetLeft.style.zIndex = '6';
                    targetLeft.classList.add('lookbook__page--prep-next-in');
                    // Force DOM reflow to apply instant prep
                    void targetLeft.offsetWidth;
                    targetLeft.classList.remove('lookbook__page--prep-next-in');
                    targetLeft.classList.add('lookbook__page--flipping-next-in');
                }
            } else {
                if (currentRight) {
                    currentRight.style.zIndex = '3';
                }
                if (currentLeft) {
                    currentLeft.style.zIndex = '5';
                    currentLeft.classList.add('lookbook__page--flipping-prev-out');
                }
                if (targetLeft) {
                    targetLeft.style.display = 'block';
                    targetLeft.style.zIndex = '2';
                }
                if (targetRight) {
                    targetRight.style.display = 'block';
                    targetRight.style.zIndex = '6';
                    targetRight.classList.add('lookbook__page--prep-prev-in');
                    // Force DOM reflow to apply instant prep
                    void targetRight.offsetWidth;
                    targetRight.classList.remove('lookbook__page--prep-prev-in');
                    targetRight.classList.add('lookbook__page--flipping-prev-in');
                }
            }

            this.currentSpread = targetSpread;

            setTimeout(() => {
                this.isFlipping = false;
                this.book.classList.remove('lookbook__book--is-flipping');
                this._render();
                // Update URL hash
                window.history.replaceState(null, '', '#page-' + (this.currentSpread + 1));

                // Announce page change to screen readers
                this._announcePageChange();

                // Dispatch event for variation navigator
                this.root.dispatchEvent(new CustomEvent('lookbook:pagechange', {
                    detail: { spread: this.currentSpread, pageIndex: this.currentSpread * 2 }
                }));
            }, 650);
        }

        /**
         * Mobile-only page transition.
         * Same parity (even→even, odd→odd) = horizontal SLIDE
         * Different parity (even→odd, odd→even) = 3D FLIP + SLIDE
         */
        _mobileFlip(targetPageIdx) {
            targetPageIdx = clamp(targetPageIdx, 0, this.pages.length - 1);
            if (targetPageIdx === this.mobileCurrentPage) return;

            this.isFlipping = true;
            const goingForward = targetPageIdx > this.mobileCurrentPage;
            const currentPage = this.pages[this.mobileCurrentPage];
            const targetPage = this.pages[targetPageIdx];

            if (!currentPage || !targetPage) {
                this.isFlipping = false;
                return;
            }

            const currentSpreadIdx = Math.floor(this.mobileCurrentPage / 2);
            const targetSpreadIdx = Math.floor(targetPageIdx / 2);
            const isSlide = (currentSpreadIdx === targetSpreadIdx);
            const flipDuration = 650;
            const slideDuration = 400;

            // Show target page
            targetPage.style.display = 'block';
            currentPage.style.display = 'block';

            if (isSlide) {
                // --- CASE 1: SLIDE (Same Spread) ---
                // The spiral moves naturally with the slide via CSS transition
                const isTargetRightPage = targetPageIdx % 2 !== 0;
                this.book.classList.toggle('lookbook__book--right-page', isTargetRightPage);

                if (goingForward) {
                    targetPage.classList.add('lookbook__page--slide-prep-right');
                    void targetPage.offsetWidth;
                    currentPage.classList.add('lookbook__page--slide-out-left');
                    targetPage.classList.remove('lookbook__page--slide-prep-right');
                    targetPage.classList.add('lookbook__page--slide-in');
                } else {
                    targetPage.classList.add('lookbook__page--slide-prep-left');
                    void targetPage.offsetWidth;
                    currentPage.classList.add('lookbook__page--slide-out-right');
                    targetPage.classList.remove('lookbook__page--slide-prep-left');
                    targetPage.classList.add('lookbook__page--slide-in');
                }

                this.mobileCurrentPage = targetPageIdx;
                this.currentSpread = targetSpreadIdx;

                setTimeout(() => {
                    this.isFlipping = false;
                    this._render();
                    window.history.replaceState(null, '', '#page-' + (this.mobileCurrentPage + 1));
                    this._announcePageChange();

                    this.root.dispatchEvent(new CustomEvent('lookbook:pagechange', {
                        detail: { spread: this.currentSpread, pageIndex: this.mobileCurrentPage }
                    }));
                }, slideDuration);
            } else {
                // --- CASE 2: 3D FLIP + BOOK SHIFT (Cross Spread) ---
                // TWO-PHASE ANIMATION:
                // PHASE A: Flip only (entire book remains centered)
                // PHASE B: Shift whole book (replaces page slide)

                const backsidePage = goingForward ? this.pages[targetPageIdx + 1] : this.pages[targetPageIdx - 1];

                // --- STEP 1: FORCE VISIBILITY CONTROL ---
                // Identify the currently peeking page so it stays visible during the flip
                // (even page → next page peeks on the right; odd page → prev page peeks on the left)
                const currentPeekingIdx = (this.mobileCurrentPage % 2 === 0)
                    ? this.mobileCurrentPage + 1
                    : this.mobileCurrentPage - 1;

                this.pages.forEach((p, idx) => {
                    // Keep the current page, its peeking neighbour, and the backside page visible
                    if (idx === this.mobileCurrentPage || idx === currentPeekingIdx || p === backsidePage) {
                        // These will be styled individually below
                    } else {
                        p.style.display = 'none';
                    }
                });

                // Ensure the peeking page stays behind all animated pages
                const peekingPage = this.pages[currentPeekingIdx];
                if (peekingPage && peekingPage !== currentPage && peekingPage !== backsidePage) {
                    peekingPage.style.display = 'block';
                    peekingPage.style.zIndex = '1';
                }

                // PHASE A: Start 3D flip (Reveal backside while flipping)
                if (goingForward) {
                    currentPage.style.display = 'block';
                    currentPage.style.zIndex = '10';
                    if (backsidePage) {
                        backsidePage.style.display = 'block';
                        backsidePage.style.zIndex = '5';
                    }
                    currentPage.classList.add('lookbook__page--mobile-flip-out-next');
                } else {
                    currentPage.style.display = 'block';
                    currentPage.style.zIndex = '10';
                    if (backsidePage) {
                        backsidePage.style.display = 'block';
                        backsidePage.style.zIndex = '5';
                    }
                    currentPage.classList.add('lookbook__page--mobile-flip-out-prev');
                }

                // --- STEP 2: POSITION TARGET PAGE CORRECTLY ---
                // We MUST explicitly place the target out of view so it can be "revealed" by the shift
                if (goingForward) {
                    targetPage.style.transform = 'translateX(-100%)';
                } else {
                    targetPage.style.transform = 'translateX(100%)';
                }

                // PHASE B: After flip is ~80% complete, shift the WHOLE book
                const slideStartTime = flipDuration * 0.8;

                setTimeout(() => {
                    this.book.classList.add('lookbook__book--sliding');

                    targetPage.style.display = 'block';
                    targetPage.style.zIndex = '15';

                    if (goingForward) {
                        // Move book RIGHT to reveal new page from left
                        this.book.classList.add('lookbook__book--shift-right');
                    } else {
                        // Move book LEFT to reveal new page from right
                        this.book.classList.add('lookbook__book--shift-left');
                    }
                }, slideStartTime);

                this.mobileCurrentPage = targetPageIdx;
                this.currentSpread = targetSpreadIdx;

                const totalDuration = slideStartTime + slideDuration;

                setTimeout(() => {
                    // 1. Grab the binding so we can freeze it too
                    const binding = this.book.querySelector('.lookbook__binding');

                    // 2. Disable transitions to snap layout instantly without the "bounce"
                    this.book.style.transition = 'none';
                    this.pages.forEach(p => p.style.transition = 'none');
                    if (binding) binding.style.transition = 'none';

                    // --- STEP 4: CLEAN UP PROPERLY ---
                    this.book.classList.remove(
                        'lookbook__book--sliding',
                        'lookbook__book--shift-right',
                        'lookbook__book--shift-left'
                    );

                    // 3. Instantly snap the spiral to the correct side BEFORE the render frame
                    const isRightPage = this.mobileCurrentPage % 2 !== 0;
                    this.book.classList.toggle('lookbook__book--right-page', isRightPage);

                    // Minimal reset - don't reset layout mid-animation
                    this.pages.forEach(p => {
                        p.style.transform = '';
                        p.style.zIndex = '';
                    });

                    this.isFlipping = false;

                    // CRITICAL FIX: Run _render() synchronously while transitions are disabled.
                    // This updates the active CSS classes immediately, completely preventing 
                    // the old page from flashing and eliminating the 50ms layout jitter.
                    this._render();

                    // 4. Force a browser reflow to apply the reset and new classes instantly
                    void this.book.offsetWidth;

                    // Final delay before full render to prevent mid-frame corruption
                    setTimeout(() => {
                        // 5. Restore original CSS transitions so future interactions animate
                        this.book.style.transition = '';
                        this.pages.forEach(p => p.style.transition = '');
                        if (binding) binding.style.transition = '';

                        window.history.replaceState(null, '', '#page-' + (this.mobileCurrentPage + 1));
                        this._announcePageChange();

                        this.root.dispatchEvent(new CustomEvent('lookbook:pagechange', {
                            detail: { spread: this.currentSpread, pageIndex: this.mobileCurrentPage }
                        }));
                    }, 50);
                }, totalDuration);
            }
        }

        _announcePageChange() {
            // Create or update announcement element
            let announcement = document.getElementById('lookbook-page-announcement');
            if (!announcement) {
                announcement = document.createElement('div');
                announcement.id = 'lookbook-page-announcement';
                announcement.setAttribute('role', 'status');
                announcement.setAttribute('aria-live', 'polite');
                announcement.setAttribute('aria-atomic', 'true');
                announcement.style.position = 'absolute';
                announcement.style.left = '-10000px';
                announcement.style.width = '1px';
                announcement.style.height = '1px';
                announcement.style.overflow = 'hidden';
                document.body.appendChild(announcement);
            }
            announcement.textContent = `Page ${this.mobileCurrentPage + 1} of ${this.pages.length}`;
        }

        _render() {
            // All mobile-specific animation class names to clean up
            const mobileClasses = [
                'lookbook__page--slide-out-left',
                'lookbook__page--slide-out-right',
                'lookbook__page--slide-prep-right',
                'lookbook__page--slide-prep-left',
                'lookbook__page--slide-in',
                'lookbook__page--mobile-flip-out-next',
                'lookbook__page--mobile-flip-prep-next',
                'lookbook__page--mobile-flip-in-next',
                'lookbook__page--mobile-flip-out-prev',
                'lookbook__page--mobile-flip-prep-prev',
                'lookbook__page--mobile-flip-in-prev'
            ];

            // Clean up book-level classes
            this.book.classList.remove('lookbook__book--sliding');

            const leftIdx = this.currentSpread * 2;
            const rightIdx = this.currentSpread * 2 + 1;

            this.pages.forEach((page, i) => {
                // Remove all flip classes
                page.classList.remove(
                    'lookbook__page--flipping-next-out',
                    'lookbook__page--flipping-next-in',
                    'lookbook__page--flipping-prev-out',
                    'lookbook__page--flipping-prev-in',
                    'lookbook__page--prep-next-in',
                    'lookbook__page--prep-prev-in',
                    'lookbook__page--active',
                    'lookbook__page--behind',
                    'lookbook__page--hidden-mobile',
                    ...mobileClasses
                );
                page.style.zIndex = '';
                page.style.transform = '';

                if (this.isMobile) {
                    // Mobile: show current linear page
                    if (i === this.mobileCurrentPage) {
                        page.classList.add('lookbook__page--active');
                        page.classList.remove('lookbook__page--next-peeking', 'lookbook__page--prev-peeking');
                        page.style.display = 'block';
                    }
                    // Peek the next page if we are on a LEFT page (even index).
                    else if (i === this.mobileCurrentPage + 1 && this.mobileCurrentPage % 2 === 0) {
                        page.classList.add('lookbook__page--next-peeking');
                        page.classList.remove('lookbook__page--prev-peeking');
                        page.style.display = 'block';
                    }
                    // NEW: Peek the previous page if we are on a RIGHT page (odd index).
                    else if (i === this.mobileCurrentPage - 1 && this.mobileCurrentPage % 2 !== 0) {
                        page.classList.add('lookbook__page--prev-peeking');
                        page.classList.remove('lookbook__page--next-peeking');
                        page.style.display = 'block';
                    }
                    else {
                        page.style.display = 'none';
                        page.classList.remove('lookbook__page--next-peeking', 'lookbook__page--prev-peeking');
                        page.classList.add('lookbook__page--hidden-mobile');
                    }
                } else {
                    // Desktop: show current spread pair
                    if (i === leftIdx || i === rightIdx) {
                        page.classList.add('lookbook__page--active');
                        page.style.display = 'block';
                    } else {
                        page.style.display = 'none';
                    }
                }
            });

            // Update dot indicators
            qsa('.lookbook__page-dot', this.root).forEach((dot, i) => {
                dot.classList.toggle('lookbook__page-dot--active', i === this.currentSpread);
            });

            // Update arrows visibility
            const prevBtn = qs('.lookbook__nav-arrow--prev', this.root);
            const nextBtn = qs('.lookbook__nav-arrow--next', this.root);
            if (this.isMobile) {
                if (prevBtn) prevBtn.style.opacity = this.mobileCurrentPage > 0 ? '1' : '0.3';
                if (nextBtn) nextBtn.style.opacity = this.mobileCurrentPage < this.pages.length - 1 ? '1' : '0.3';
            } else {
                if (prevBtn) prevBtn.style.opacity = this.currentSpread > 0 ? '1' : '0.3';
                if (nextBtn) nextBtn.style.opacity = this.currentSpread < this.totalSpreads - 1 ? '1' : '0.3';
            }

            // Ensure spiral is correct on page load and window resize
            if (this.isMobile) {
                const isRightPage = this.mobileCurrentPage % 2 !== 0;
                this.book.classList.toggle('lookbook__book--right-page', isRightPage);
            }

            // Trigger lazy loading for visible pages
            this._lazyLoad(this.isMobile ? this.mobileCurrentPage : leftIdx, rightIdx);
        }

        _lazyLoad(leftIdx, rightIdx) {
            // Load current + adjacent spreads
            const pagesToLoad = [leftIdx, rightIdx, leftIdx + 2, rightIdx + 2, leftIdx - 2, rightIdx - 2];
            pagesToLoad.forEach(idx => {
                if (idx >= 0 && idx < this.pages.length) {
                    qsa('img[data-src]', this.pages[idx]).forEach(img => {
                        if (img.dataset.src) {
                            img.src = img.dataset.src;
                            img.removeAttribute('data-src');
                        }
                    });
                }
            });
        }
    }

    /* =========================================
       VARIATION NAVIGATOR
       ========================================= */

    class VariationNavigator {
        constructor(rootEl, flipEngine) {
            this.root = rootEl;
            this.flipEngine = flipEngine;

            // Variation items (.lookbook__variation-item) now behave as standard <a> tags 
            // driven by Shopify Liquid routing, so we no longer e.preventDefault() them here.

            // Keep swatch logic for in-page jumping (Product Panel -> Book)
            qsa('.lookbook__variation-swatch', rootEl).forEach(swatch => {
                swatch.addEventListener('click', (e) => {
                    e.preventDefault();
                    // Update active state
                    qsa('.lookbook__variation-swatch', rootEl).forEach(s =>
                        s.classList.remove('lookbook__variation-swatch--active')
                    );
                    swatch.classList.add('lookbook__variation-swatch--active');

                    // Navigate to linked page
                    const targetPage = parseInt(swatch.dataset.targetPage);
                    if (!isNaN(targetPage) && this.flipEngine) {
                        // Convert page index to spread index
                        const spreadIdx = Math.floor(targetPage / 2);
                        this.flipEngine.goTo(spreadIdx);
                    }
                });
            });
        }
    }

    /* =========================================
       USER UPLOAD
       ========================================= */

    class UserUpload {
        constructor(rootEl) {
            const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

            qsa('.lookbook__element--upload', rootEl).forEach(uploadEl => {
                const input = qs('.lookbook__upload-input', uploadEl);
                const icon = qs('.lookbook__upload-icon', uploadEl);
                const textEl = qs('.lookbook__upload-text', uploadEl);

                if (!input) return;

                uploadEl.addEventListener('click', () => input.click());

                input.addEventListener('change', (e) => {
                    const file = e.target.files[0];

                    // Validate file type
                    if (!file || !file.type.startsWith('image/')) {
                        console.warn('Invalid file type. Please select an image.');
                        return;
                    }

                    // Validate file size
                    if (file.size > MAX_FILE_SIZE) {
                        console.warn('File too large. Maximum size is 5MB.');
                        return;
                    }

                    const reader = new FileReader();

                    reader.onerror = () => {
                        console.error('Failed to read file');
                    };

                    reader.onload = (ev) => {
                        // Replace content with preview
                        uploadEl.innerHTML = '';
                        const img = document.createElement('img');
                        img.src = ev.target.result;
                        img.className = 'lookbook__upload-preview';
                        img.alt = 'Your uploaded image';
                        uploadEl.appendChild(img);
                        uploadEl.style.border = 'none';
                        uploadEl.style.background = 'transparent';
                        uploadEl.style.padding = '0';
                    };

                    reader.readAsDataURL(file);
                });
            });
        }
    }

    /* =========================================
       EDITOR OVERLAY
       ========================================= */

    class EditorOverlay {
        constructor(rootEl) {
            this.root = rootEl;
            this.isActive = false;
            this.selectedEl = null;
            this.undoStack = [];
            this.redoStack = [];
            this.snapToGrid = false;
            this.gridSize = 20;
            this.dragState = null;

            this.toggleBtn = qs('.lookbook__editor-toggle', rootEl);
            if (this.toggleBtn) {
                this.toggleBtn.addEventListener('click', () => this.toggle());
            }

            // Toolbar buttons
            qs('.lookbook__editor-btn[data-action="snap"]', rootEl)?.addEventListener('click', (e) => {
                this.snapToGrid = !this.snapToGrid;
                e.target.classList.toggle('lookbook__editor-btn--active', this.snapToGrid);
            });

            qs('.lookbook__editor-btn[data-action="undo"]', rootEl)?.addEventListener('click', () => this.undo());
            qs('.lookbook__editor-btn[data-action="redo"]', rootEl)?.addEventListener('click', () => this.redo());
            qs('.lookbook__editor-btn[data-action="export"]', rootEl)?.addEventListener('click', () => this.exportJSON());
            qs('.lookbook__editor-btn[data-action="z-up"]', rootEl)?.addEventListener('click', () => this.changeZ(1));
            qs('.lookbook__editor-btn[data-action="z-down"]', rootEl)?.addEventListener('click', () => this.changeZ(-1));
        }

        toggle() {
            this.isActive = !this.isActive;
            this.root.classList.toggle('lookbook--editing', this.isActive);

            if (this.isActive) {
                this._enableDrag();
            } else {
                this._disableDrag();
                this.selectedEl = null;
            }
        }

        _enableDrag() {
            this._boundPointerDown = (e) => this._onPointerDown(e);
            this._boundPointerMove = (e) => this._onPointerMove(e);
            this._boundPointerUp = (e) => this._onPointerUp(e);

            qsa('.lookbook__element', this.root).forEach(el => {
                el.addEventListener('pointerdown', this._boundPointerDown);
            });
            document.addEventListener('pointermove', this._boundPointerMove);
            document.addEventListener('pointerup', this._boundPointerUp);
        }

        _disableDrag() {
            qsa('.lookbook__element', this.root).forEach(el => {
                el.removeEventListener('pointerdown', this._boundPointerDown);
            });
            document.removeEventListener('pointermove', this._boundPointerMove);
            document.removeEventListener('pointerup', this._boundPointerUp);
        }

        _onPointerDown(e) {
            if (!this.isActive) return;
            const el = e.target.closest('.lookbook__element');
            if (!el) return;

            e.preventDefault();
            this.selectedEl = el;

            // Highlight selection
            qsa('.lookbook__element', this.root).forEach(item =>
                item.style.outline = '1px dashed rgba(203,27,3,0.4)'
            );
            el.style.outline = '2px solid #CB1B03';

            const page = el.closest('.lookbook__page');
            if (!page) return;

            const pageRect = page.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();

            // Save initial state for undo
            this._saveState(el);

            this.dragState = {
                el,
                page,
                pageRect,
                startX: e.clientX,
                startY: e.clientY,
                startLeft: ((elRect.left - pageRect.left) / pageRect.width) * 100,
                startTop: ((elRect.top - pageRect.top) / pageRect.height) * 100,
            };
        }

        _onPointerMove(e) {
            if (!this.dragState) return;
            e.preventDefault();

            requestAnimationFrame(() => {
                if (!this.dragState) return;

                const { el, pageRect, startX, startY, startLeft, startTop } = this.dragState;
                const dx = e.clientX - startX;
                const dy = e.clientY - startY;

                let newLeft = startLeft + (dx / pageRect.width) * 100;
                let newTop = startTop + (dy / pageRect.height) * 100;

                if (this.snapToGrid) {
                    const snapPct = (this.gridSize / pageRect.width) * 100;
                    newLeft = Math.round(newLeft / snapPct) * snapPct;
                    const snapPctY = (this.gridSize / pageRect.height) * 100;
                    newTop = Math.round(newTop / snapPctY) * snapPctY;
                }

                newLeft = clamp(newLeft, 0, 90);
                newTop = clamp(newTop, 0, 90);

                el.style.setProperty('--el-x', newLeft + '%');
                el.style.setProperty('--el-y', newTop + '%');
            });
        }

        _onPointerUp() {
            this.dragState = null;
        }

        _saveState(el) {
            this.undoStack.push({
                id: el.dataset.blockId,
                x: el.style.getPropertyValue('--el-x'),
                y: el.style.getPropertyValue('--el-y'),
                z: el.style.getPropertyValue('--el-z'),
                rotate: el.style.getPropertyValue('--el-rotate'),
            });

            // Limit stack size to 20 operations
            if (this.undoStack.length > 20) {
                this.undoStack.shift();
            }

            this.redoStack = [];
        }

        undo() {
            if (this.undoStack.length === 0) return;
            const state = this.undoStack.pop();
            const el = qs(`[data-block-id="${state.id}"]`, this.root);
            if (!el) return;

            // Save current state to redo
            this.redoStack.push({
                id: el.dataset.blockId,
                x: el.style.getPropertyValue('--el-x'),
                y: el.style.getPropertyValue('--el-y'),
                z: el.style.getPropertyValue('--el-z'),
                rotate: el.style.getPropertyValue('--el-rotate'),
            });

            if (state.x) el.style.setProperty('--el-x', state.x);
            if (state.y) el.style.setProperty('--el-y', state.y);
            if (state.z) el.style.setProperty('--el-z', state.z);
            if (state.rotate) el.style.setProperty('--el-rotate', state.rotate);
        }

        redo() {
            if (this.redoStack.length === 0) return;
            const state = this.redoStack.pop();
            const el = qs(`[data-block-id="${state.id}"]`, this.root);
            if (!el) return;

            this.undoStack.push({
                id: el.dataset.blockId,
                x: el.style.getPropertyValue('--el-x'),
                y: el.style.getPropertyValue('--el-y'),
                z: el.style.getPropertyValue('--el-z'),
                rotate: el.style.getPropertyValue('--el-rotate'),
            });

            if (state.x) el.style.setProperty('--el-x', state.x);
            if (state.y) el.style.setProperty('--el-y', state.y);
            if (state.z) el.style.setProperty('--el-z', state.z);
            if (state.rotate) el.style.setProperty('--el-rotate', state.rotate);
        }

        changeZ(delta) {
            if (!this.selectedEl) return;
            this._saveState(this.selectedEl);
            const current = parseInt(this.selectedEl.style.getPropertyValue('--el-z') || '5');
            this.selectedEl.style.setProperty('--el-z', clamp(current + delta, 1, 20));
        }

        exportJSON() {
            const pages = [];
            qsa('.lookbook__page', this.root).forEach((page, pIdx) => {
                const elements = [];
                qsa('.lookbook__element', page).forEach(el => {
                    elements.push({
                        id: el.dataset.blockId || '',
                        type: el.dataset.blockType || '',
                        x: el.style.getPropertyValue('--el-x') || el.dataset.x || '',
                        y: el.style.getPropertyValue('--el-y') || el.dataset.y || '',
                        width: el.style.getPropertyValue('--el-w') || '',
                        height: el.style.getPropertyValue('--el-h') || '',
                        rotation: el.style.getPropertyValue('--el-rotate') || '0deg',
                        zIndex: el.style.getPropertyValue('--el-z') || '5',
                    });
                });
                pages.push({ index: pIdx, elements });
            });

            const json = JSON.stringify({ pages }, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'lookbook-layout.json';
            a.click();
            URL.revokeObjectURL(url);
        }
    }

    /* =========================================
       PRODUCT PANEL LOGIC
       ========================================= */

    class ProductPanel {
        constructor(rootEl) {
            this.root = rootEl;
            this.panel = qs('.lookbook__product-panel', rootEl);
            if (!this.panel) return;

            this._initQuantity();
            this._initSizes();
            this._initBuyNow();
        }

        _initQuantity() {
            const minus = qs('.lookbook__qty-btn[data-action="minus"]', this.panel);
            const plus = qs('.lookbook__qty-btn[data-action="plus"]', this.panel);
            const input = qs('.lookbook__qty-input', this.panel);
            if (!input) return;

            minus?.addEventListener('click', () => {
                const v = parseInt(input.value) || 1;
                if (v > 1) input.value = v - 1;
            });
            plus?.addEventListener('click', () => {
                const v = parseInt(input.value) || 1;
                input.value = v + 1;
            });
        }

        _initSizes() {
            qsa('.lookbook__size-btn', this.panel).forEach(btn => {
                btn.addEventListener('click', () => {
                    qsa('.lookbook__size-btn', this.panel).forEach(b =>
                        b.classList.remove('lookbook__size-btn--active')
                    );
                    btn.classList.add('lookbook__size-btn--active');
                });
            });
        }

        _initBuyNow() {
            const buyBtn = qs('.lookbook__buy-now', this.panel);
            const sizeModal = qs('.lookbook__size-modal-overlay', this.root);
            if (!buyBtn) return;

            buyBtn.addEventListener('click', (e) => {
                // Check if size is selected
                const activeSize = qs('.lookbook__size-btn--active', this.panel);
                if (!activeSize && sizeModal) {
                    e.preventDefault();
                    sizeModal.classList.add('lookbook__size-modal-overlay--open');
                }
            });

            // Close modal
            sizeModal?.addEventListener('click', (e) => {
                if (e.target === sizeModal || e.target.classList.contains('lookbook__size-modal-close')) {
                    sizeModal.classList.remove('lookbook__size-modal-overlay--open');
                }
            });

            // Modal size selection
            qsa('.lookbook__size-btn', sizeModal).forEach(btn => {
                btn.addEventListener('click', () => {
                    // Mirror selection to main panel
                    const value = btn.textContent.trim();
                    qsa('.lookbook__size-btn', this.panel).forEach(b => {
                        b.classList.toggle('lookbook__size-btn--active', b.textContent.trim() === value);
                    });
                    sizeModal?.classList.remove('lookbook__size-modal-overlay--open');
                });
            });
        }
    }

    /* =========================================
       ADD TO CART
       ========================================= */

    class AddToCart {
        constructor(rootEl) {
            const form = qs('.lookbook__cart-form', rootEl);
            if (!form) return;

            form.addEventListener('submit', (e) => {
                // Let Shopify handle natively unless we want AJAX
                // Could add AJAX cart here for SPA feel
            });
        }
    }

    /* =========================================
       DYNAMIC BACKGROUND (LOCKED LAYOUT)
       ========================================= */

    class DynamicBg {
        constructor(rootEl) {
            this.root = rootEl;
            this.book = qs('.lookbook__book', rootEl);
            this.bg = qs('.lookbook-dynamic-bg', rootEl) || qs('.lookbook-dynamic-bg', document.body);
            if (!this.book || !this.bg) return;

            // Move bg to document body to escape Shopify container clipping and CSS transforms
            if (this.bg.parentElement !== document.body) {
                document.body.appendChild(this.bg);
            }

            this._update = this._update.bind(this);
            window.addEventListener('resize', this._update);

            // Initial updates
            requestAnimationFrame(this._update);
            setTimeout(this._update, 100);
            setTimeout(this._update, 500);
        }

        _update() {
            if (window.innerWidth < 1024) {
                this.bg.style.top = '';
                this.bg.style.left = '';
                this.bg.style.width = '';
                this.bg.style.height = '';
                return;
            }

            // FIX 1: Reset height to 0 before measuring the document.
            // This stops the background from holding the scrollHeight open,
            // guaranteeing the 20px bottom margin never infinitely expands.
            this.bg.style.height = '0px';

            const rect = this.book.getBoundingClientRect();
            const scrollTop = window.scrollY;

            // Book position in document
            const bookTop = rect.top + scrollTop;

            // Now calculate the TRUE page height after the DOM has collapsed naturally
            const pageHeight = document.documentElement.scrollHeight;

            // Width: from left to slightly before book right edge
            const bgWidth = rect.right - 60;

            this.bg.style.top = bookTop + 'px';
            this.bg.style.left = '0px';
            this.bg.style.width = bgWidth + 'px';

            // Extend to page end (covers the book + the exact 20px padding)
            this.bg.style.height = (pageHeight - bookTop) + 'px';
        }
    }

    class FixedUIBinder {
        constructor(rootEl) {
            this.root = rootEl;
            this.book = this.root.querySelector('.lookbook__book');
            this.nav = this.root.querySelector('.lookbook__nav');
            this.variations = this.root.querySelector('.lookbook__variations-panel');

            if (!this.book || !this.nav) return;

            this.update = this.update.bind(this);
            window.addEventListener('resize', this.update, { passive: true });

            this.update();
        }

        update() {
            if (window.innerWidth < 1024) {
                if (this.nav) {
                    this.nav.style.left = '';
                    this.nav.style.top = '';
                    this.nav.style.transform = '';
                }
                if (this.variations) {
                    this.variations.style.left = '';
                    this.variations.style.top = '';
                    this.variations.style.bottom = '';
                }
                return;
            }

            const rect = this.book.getBoundingClientRect();
            const rootRect = this.root.getBoundingClientRect();

            const overlap = 20; // 👈 controls how much buttons go INSIDE book

            const navLeft = (rect.right - rootRect.left) - overlap; // 👈 relative to root
            const navTop = (rect.top - rootRect.top) + 20; // 👈 relative to root

            if (this.nav) {
                this.nav.style.left = navLeft + 'px';
                this.nav.style.top = navTop + 'px';
                this.nav.style.transform = 'none'; // ❌ remove centering
            }

            if (this.variations) {
                this.variations.style.left = navLeft + 'px';

                // position 64px below the nav tabs
                const navBottom = this.nav.getBoundingClientRect().bottom - rootRect.top;
                this.variations.style.top = (navBottom + 16) + 'px';
                this.variations.style.bottom = 'auto';
            }
        }
    }

    /* =========================================
       FLUID SCALE ENGINE (UPDATED)
       ========================================= */

    class PageScaleEngine {
        constructor(rootEl) {
            this.book = rootEl.querySelector('.lookbook__book');
            if (!this.book) return;

            this.updateScale = this.updateScale.bind(this);
            this.ro = new ResizeObserver(this.updateScale);

            // FIX: Observe the book container instead of the first page.
            // The book never gets 'display: none', so its width never drops to 0.
            this.ro.observe(this.book);

            // Set immediately to prevent initial layout flash
            const initialWidth = this.book.getBoundingClientRect().width;
            if (initialWidth > 0) {
                this._applyScale(initialWidth);
            }
        }

        updateScale(entries) {
            for (let entry of entries) {
                this._applyScale(entry.contentRect.width);
            }
        }

        _applyScale(bookWidth) {
            if (bookWidth <= 0) return; // Safeguard against 0 scale

            const isMobile = window.innerWidth < 1024;

            // Calculate actual page width based on your CSS rules:
            // Desktop pages are 50% of the book, Mobile pages are 85%
            const pageWidth = isMobile ? bookWidth * 0.85 : bookWidth * 0.5;

            // 425px is the baseline design width
            const scale = pageWidth / 425;

            this.book.style.setProperty('--page-scale', scale);
        }
    }

    /* =========================================
       COPY TO CLIPBOARD
       ========================================= */

    class CopyToClipboard {
        constructor(rootEl) {
            this.buttons = qsa('.lookbook__copy-btn', rootEl);

            this.buttons.forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    // STOP the click from bubbling up to the book
                    e.preventDefault();
                    e.stopPropagation();

                    const text = (btn.dataset.copy || '').trim();
                    if (!text) return;

                    const originalText = btn.innerHTML;

                    // Helper function for the legacy fallback
                    const executeFallbackCopy = () => {
                        const textarea = document.createElement('textarea');
                        textarea.value = text;
                        textarea.setAttribute('readonly', ''); // Prevents mobile keyboard from popping up
                        textarea.style.position = 'fixed';
                        textarea.style.left = '-9999px'; // Move fully off-screen
                        document.body.appendChild(textarea);
                        textarea.focus();
                        textarea.select();
                        const successful = document.execCommand('copy');
                        document.body.removeChild(textarea);
                        if (!successful) throw new Error('execCommand fallback failed');
                    };

                    try {
                        if (navigator.clipboard && window.isSecureContext) {
                            try {
                                // Try the modern API first
                                await navigator.clipboard.writeText(text);
                            } catch (clipboardError) {
                                // If blocked by iframe permissions (Shopify Editor), use fallback
                                console.warn('Modern clipboard blocked, using fallback.', clipboardError);
                                executeFallbackCopy();
                            }
                        } else {
                            // If not secure or no clipboard API, use fallback
                            executeFallbackCopy();
                        }

                        // Success UI Feedback
                        btn.innerHTML = '✓';
                        btn.style.color = '#1FAF38';
                        btn.style.borderColor = '#1FAF38';

                        setTimeout(() => {
                            btn.innerHTML = originalText;
                            btn.style.color = '';
                            btn.style.borderColor = '';
                        }, 1500);

                    } catch (err) {
                        // Total failure (both modern and fallback failed)
                        console.error('Copy failed completely:', err);
                        btn.innerHTML = '✗';
                        btn.style.color = '#CB1B03';
                        btn.style.borderColor = '#CB1B03';
                        setTimeout(() => {
                            btn.innerHTML = originalText;
                            btn.style.color = '';
                            btn.style.borderColor = '';
                        }, 1500);
                    }
                });
            });
        }
    }

    /* =========================================
       INIT
       ========================================= */

    function initLookbook() {
        const roots = qsa('.lookbook');
        roots.forEach(root => {
            const flipEngine = new PageFlipEngine(root);
            new VariationNavigator(root, flipEngine);
            new UserUpload(root);
            new EditorOverlay(root);
            new ProductPanel(root);
            new AddToCart(root);
            new DynamicBg(root);
            new FixedUIBinder(root);
            new PageScaleEngine(root);
            new CopyToClipboard(root);
        });


    }

    // Init on load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initLookbook);
    } else {
        initLookbook();
    }

})();
